// @mui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// components
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { invitationApi } from 'src/api/invitation';
import FormProvider, {
  RHFMultiSelect,
  RHFTextField,
} from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { Roles } from 'src/types/User';
import { CreateInvitationDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';

// ----------------------------------------------------------------------

export const ROLES = [
  { value: Roles.organization_owner, label: translateKey("common.owner") },
  { value: Roles.organization_admin, label: translateKey("systemPages.roleAdminLabel") },
  { value: Roles.professional, label: translateKey("search.book.cardSpecialist") },
];

const NON_ADMIN_ROLES = [
  { value: Roles.organization_admin, label: translateKey("systemPages.roleAdminLabel") },
  { value: Roles.professional, label: translateKey("search.book.cardSpecialist") },
];

export type OrganizationInvitesAddModalFormValuesProps = {
  firstName: string;
  lastName: string;
  email: string;
  roles: CreateInvitationDto['roles'];
};

type OrganizationInvitesAddModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fetchInvites: () => void;
};

export default function OrganizationInvitesAddModal({
  isOpen,
  onClose,
  fetchInvites,
}: OrganizationInvitesAddModalProps) {
  const { organizationId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: addOrganizationUser, isLoading: isAddLoading } =
    useMutation({
      mutationFn: (createInvitationDto: CreateInvitationDto) =>
        invitationApi.invitationControllerCreateInvitation({
          createInvitationDto,
        }),
    });

  const AddOrganizationSchema: Yup.Schema<OrganizationInvitesAddModalFormValuesProps> =
    Yup.object().shape({
      firstName: Yup.string().required(translateKey("visit.validationNameRequired")),
      lastName: Yup.string().required(translateKey("validation.lastNameRequired")),
      email: Yup.string()
        .email(translateKey("hardcoded.email_jest_nieprawidlowy"))
        .required('Email jest wymagany'),
      roles: Yup.array()
        .min(1, translateKey("hardcoded.wybierz_co_najmniej_jedna_role"))
        .required(translateKey("hardcoded.role_sa_wymagane")),
    });

  const defaultValues: OrganizationInvitesAddModalFormValuesProps = {
    firstName: '',
    lastName: '',
    email: '',
    roles: [],
  };

  const methods = useForm<OrganizationInvitesAddModalFormValuesProps>({
    resolver: yupResolver(AddOrganizationSchema),
    defaultValues,
    values: defaultValues,
  });

  const { handleSubmit, reset } = methods;

  const handleOnSubmit = async (
    data: OrganizationInvitesAddModalFormValuesProps,
  ) => {
    if (organizationId) {
      try {
        const response = await addOrganizationUser({
          ...data,
          organizationId: organizationId,
        });
        fetchInvites();
        onClose();
        if ((response as any)?.isReminder) {
          enqueueSnackbar(
            translateKey("hardcoded.ten_uzytkownik_posiada_juz_zaproszenie_do_twojej_placowki"),
            { variant: 'success' },
          );
        } else {
          enqueueSnackbar(translateKey("hardcoded.zaproszenie_zostalo_wyslane"), {
            variant: 'success',
          });
        }
      } catch (error) {
        enqueueSnackbar(error?.response?.data?.message || error.message, {
          variant: 'error',
        });
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            width: {
              sm: '100%',
              md: '540px',
              lg: '640px',
              xl: '640px',
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
      >{translateKey("hardcoded.zapros_uzytkownika")}<Box flex={1} />
        <IconButton aria-label="close" onClick={onClose}>
          <Iconify icon="eva:close-fill" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(handleOnSubmit)}>
        <DialogContent>
          <Stack sx={{ pt: 2, pb: 2, gap: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: { xs: 3, md: 2 },
              }}
            >
              <RHFTextField
                required
                name="firstName"
                label={translateKey("quickBooking.petNameField")}
                disabled={isAddLoading}
              />

              <RHFTextField
                required
                name="lastName"
                label={translateKey("auth.fieldLastName")}
                disabled={isAddLoading}
              />
              <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                <RHFMultiSelect
                  chip
                  required
                  name="roles"
                  label="Role"
                  disabled={isAddLoading}
                  options={NON_ADMIN_ROLES}
                  sx={{ width: '100%' }}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                <RHFTextField
                  required
                  type="email"
                  name="email"
                  label="Adres email"
                  disabled={isAddLoading}
                  helperText={translateKey("hardcoded.na_podany_adres_email_zostanie_wyslane_zaproszenie_do")}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>{translateKey("common.cancel")}</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isAddLoading}
          >{translateKey("common.add")}</LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
